import { useEffect, useContext, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../../constants/constants';
import { OrgLevelSettingsContext } from './NROrgLevelSettingsContextWrapper';
import useNRLazyQuery from '../../../graphql/useNRLazyQuery';
import { buildGetOrgLevelSettings } from '../../../graphql/queries/orgs';
import isEqual from 'lodash.isequal';

const ADVANCED_SETTINGS_POLL_INTERVAL_MS = 30_000;

const useFetchOrgLevelSettings = () => {
  const { pathname } = useLocation();
  const { setOrgLevelSettings, orgLevelSettings, refetch, setRefetch } = useContext(OrgLevelSettingsContext);

  const { value: isRiseAiConversationalGptEnabled } = useFlag(FEATURE_FLAGS.RISE_AI_CONVERSATIONAL_GPT_SETTINGS, false);
  const { value: isRiseAIInsightsReportEnabled } = useFlag(FEATURE_FLAGS.RISE_AI_INSIGHTS_REPORT_SETTINGS, false);
  const { value: isAutoRemediationsSettingsEnabled } = useFlag(FEATURE_FLAGS.AUTO_REMEDIATIONS_SETTINGS, false);
  const { value: isSecretScanningSettingsEnabled } = useFlag(FEATURE_FLAGS.SECRET_SCANNING_SETTINGS, false);
  const { value: isAdvancedSettingsEnabled } = useFlag(FEATURE_FLAGS.ADVANCED_SETTINGS, false);

  const GET_ORG_LEVEL_SETTINGS = useMemo(
    () =>
      buildGetOrgLevelSettings({
        isRiseAiConversationalGptEnabled,
        isRiseAIInsightsReportEnabled,
        isAutoRemediationsSettingsEnabled,
        isSecretScanningSettingsEnabled,
        isAdvancedSettingsEnabled
      }),
    [
      isRiseAiConversationalGptEnabled,
      isRiseAIInsightsReportEnabled,
      isAutoRemediationsSettingsEnabled,
      isSecretScanningSettingsEnabled,
      isAdvancedSettingsEnabled
    ]
  );

  const isOnGeneralSettingsPage = pathname === '/settings/General';
  const shouldPollAdvancedSettings =
    isOnGeneralSettingsPage && isAdvancedSettingsEnabled && orgLevelSettings?.advancedSettingsJobsProcessing === true;

  const prevQueryRef = useRef(GET_ORG_LEVEL_SETTINGS);
  const queryStructureChanged = prevQueryRef.current !== GET_ORG_LEVEL_SETTINGS;

  const [, , called, makeRequest, , , startPolling, stopPolling] = useNRLazyQuery({
    query: GET_ORG_LEVEL_SETTINGS,
    options: {
      fetchPolicy: 'cache-first'
    },
    qlObjKey: 'orgLevelSettings',
    transformData: data => {
      if (!isEqual(orgLevelSettings, data)) {
        setOrgLevelSettings(data);
      }
    }
  });

  useEffect(() => {
    const isInitialLoad = !orgLevelSettings && !called;
    const needsRefetchForNewFields = queryStructureChanged && (orgLevelSettings || called);

    const shouldFetch = isInitialLoad || refetch || needsRefetchForNewFields;
    if (!shouldFetch) return;

    if (queryStructureChanged) {
      prevQueryRef.current = GET_ORG_LEVEL_SETTINGS;
    }
    setRefetch(false);
    makeRequest({
      fetchPolicy: refetch || queryStructureChanged ? 'network-only' : 'cache-first'
    });
  }, [refetch, orgLevelSettings, called, GET_ORG_LEVEL_SETTINGS, queryStructureChanged]);

  const wasPollingRef = useRef(false);

  useEffect(() => {
    if (shouldPollAdvancedSettings) {
      if (!called || !wasPollingRef.current) {
        wasPollingRef.current = true;
        makeRequest({ fetchPolicy: 'network-only' });
      }
      startPolling(ADVANCED_SETTINGS_POLL_INTERVAL_MS);
    } else {
      wasPollingRef.current = false;
      stopPolling();
    }
    return () => stopPolling();
  }, [shouldPollAdvancedSettings, called, startPolling, stopPolling, makeRequest]);
};

export default useFetchOrgLevelSettings;
